import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AssignRouteModal, {
  AssignRouteFormValues,
} from "app/components/Modal/AssignRouteModal";
import MoveDeliveryAreaModal, {
  PendingDeliveryAreaMove,
} from "app/components/Modal/MoveDeliveryAreaModal";
import { MobilePageHeader } from "app/mobile/components/MobilePageHeader";
import { KnowledgePanel } from "app/components/Knowledge/KnowledgePanel";
import { MobileOrderCard } from "app/mobile/components/MobileOrderCard";
import {
  MobileAreaTabs,
  MobileMoveAreaDrawer,
  MobileOrdersFilterDrawer,
} from "app/mobile/components/MobileOrdersUi";
import { useAuthContext } from "app/components/Auth/AuthContext";
import {
  AuthData,
  AuthExpiredError,
  clearAuthData,
  getOdooApiHeaders,
  getStoredAuthData,
  isEmbeddedMode,
  odooApiFetch,
  recoverAuth,
  subscribeToAuthUpdates,
} from "app/utils/auth";
import { normalizeBoardStages } from "app/utils/boardStages";
import {
  buildDeliveryPartnersUrl,
  extractOdooResults,
  serializeOdooFilters,
  splitDeliveryPartners,
  DeliveryPartner,
  updatePickingDeliveryArea,
  assignPickingsToRoute,
} from "app/utils/odooApi";
import { handleApiError } from "app/utils/apiErrorHandler";
import { buildAssignPickingsPayload } from "app/utils/assignPickings";
import { BRANDING } from "app/utils/branding";
import {
  getUserTimezone,
} from "app/utils/timezone";
import {
  DATE_RANGE_PRESET_OPTIONS,
  DateRangePresetId,
  getDateRangeForPreset,
} from "app/utils/dateRangePresets";
import {
  getInitialDeliveryPartnersState,
  saveDeliveryPartnersToStorage,
} from "app/utils/deliveryPartnersCache";
import {
  AssignmentFilter,
  buildPickingFilters,
  buildPickingOrdersTableData,
  filterPickingOrders,
} from "app/utils/ordersBoard";

const extractPickingResults = extractOdooResults;

export function MobileOrdersPage() {
  const navigate = useNavigate();
  const embedded = isEmbeddedMode();
  const contextAuth = useAuthContext();

  const [authData, setAuthData] = useState<AuthData>(
    () => contextAuth || getStoredAuthData() || {}
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const access_token = authData.access_token ?? "";
  const userTimezone = getUserTimezone(authData);

  const [deliveryAreaData, setDeliveryAreaData] = useState<any[]>([]);
  const [pickingOrdersData, setPickingOrdersData] = useState<any[]>([]);
  const [retrieveDriversData, setRetrieveDriversData] = useState<any[]>(() =>
    getInitialDeliveryPartnersState().drivers
  );
  const [retrieveRoutesData, setRetrieveRoutesData] = useState<any[]>([]);
  const [retrieveDepotsData, setRetrieveDepotsData] = useState<any[]>(() =>
    getInitialDeliveryPartnersState().depots
  );

  const [isBoardBusy, setIsBoardBusy] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>("unassigned");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(() => {
    const { start } = getDateRangeForPreset(
      "current_month",
      getUserTimezone(getStoredAuthData())
    );
    return start;
  });
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(() => {
    const { end } = getDateRangeForPreset(
      "current_month",
      getUserTimezone(getStoredAuthData())
    );
    return end;
  });
  const [dateRangePreset, setDateRangePreset] =
    useState<DateRangePresetId | "">("current_month");

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | string | null>(
    null
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [moveDrawerOpen, setMoveDrawerOpen] = useState(false);
  const [moveTargetItem, setMoveTargetItem] = useState<any | null>(null);
  const [pendingMove, setPendingMove] =
    useState<PendingDeliveryAreaMove | null>(null);
  const [isUpdatingDeliveryArea, setIsUpdatingDeliveryArea] = useState(false);
  const [openAssignRouteModal, setOpenAssignRouteModal] = useState(false);
  const [isAssigningRoute, setIsAssigningRoute] = useState(false);
  const [isRefreshingAssignData, setIsRefreshingAssignData] = useState(false);

  const pickingFetchRequestRef = useRef(0);

  useEffect(() => {
    if (contextAuth?.access_token) {
      setAuthData(contextAuth);
      setSessionExpired(false);
    }
  }, [contextAuth]);

  useEffect(
    () =>
      subscribeToAuthUpdates((nextAuth) => {
        if (nextAuth) {
          setAuthData(nextAuth);
          setSessionExpired(false);
        }
      }),
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleSessionExpired = useCallback(async (): Promise<boolean> => {
    if (embedded) {
      const recovered = await recoverAuth();
      if (recovered?.access_token) {
        setSessionExpired(false);
        return true;
      }
      setSessionExpired(true);
      toast.error(BRANDING.sessionExpiredRefreshPageSignIn, {
        autoClose: false,
      });
      return false;
    }
    clearAuthData();
    navigate("/login");
    return false;
  }, [embedded, navigate]);

  const filteredOrders = useMemo(
    () =>
      filterPickingOrders(pickingOrdersData, {
        search: debouncedSearch,
        assignmentFilter,
      }),
    [pickingOrdersData, debouncedSearch, assignmentFilter]
  );

  const stages = useMemo(
    () =>
      normalizeBoardStages(
        buildPickingOrdersTableData(deliveryAreaData, filteredOrders)
      ),
    [deliveryAreaData, filteredOrders]
  );

  const sortedStages = useMemo(
    () =>
      [...stages].sort(
        (a, b) => (b.items?.length ?? 0) - (a.items?.length ?? 0)
      ),
    [stages]
  );

  useEffect(() => {
    setSelectedStageId(null);
    setSelectedIds([]);
  }, [debouncedSearch, assignmentFilter, fromDate, toDate]);

  useEffect(() => {
    if (selectedStageId == null) {
      return;
    }
    if (
      !sortedStages.some((stage) => String(stage.id) === String(selectedStageId))
    ) {
      setSelectedStageId(null);
    }
  }, [sortedStages, selectedStageId]);

  const activeStage = useMemo(
    () =>
      sortedStages.find((stage) => String(stage.id) === String(selectedStageId)) ??
      null,
    [sortedStages, selectedStageId]
  );

  const fetchPickingOrdersWithFilters = async (filters: unknown[]) => {
    if (!access_token || sessionExpired) {
      return [];
    }
    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/stock.picking/?filters=${encodeURIComponent(
          serializeOdooFilters(filters as unknown[][])
        )}`,
        access_token
      );
      return extractPickingResults(parsed);
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        const recovered = await handleSessionExpired();
        if (recovered) {
          return [];
        }
      }
      throw error;
    }
  };

  const fetchPickingOrdersForDateRange = async (
    from: dayjs.Dayjs,
    to: dayjs.Dayjs
  ) => {
    const requestId = ++pickingFetchRequestRef.current;
    setIsBoardBusy(true);
    try {
      const results = await fetchPickingOrdersWithFilters(
        buildPickingFilters({
          startDate: from.startOf("day"),
          endDate: to.startOf("day"),
          timezone: userTimezone,
        })
      );
      if (requestId !== pickingFetchRequestRef.current) {
        return;
      }
      setPickingOrdersData(results);
    } catch (error) {
      console.error(error);
      if (requestId === pickingFetchRequestRef.current) {
        setPickingOrdersData([]);
      }
    } finally {
      if (requestId === pickingFetchRequestRef.current) {
        setIsBoardBusy(false);
      }
    }
  };

  const getDeliveryAreaApi = async () => {
    if (!access_token || sessionExpired) {
      return;
    }
    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.area`,
        access_token
      );
      setDeliveryAreaData(extractOdooResults(parsed));
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
      }
    }
  };

  const getRetrieveDeliveryPartners = async () => {
    if (!access_token || sessionExpired) {
      return;
    }
    try {
      const parsed = await odooApiFetch<{ results?: DeliveryPartner[] }>(
        buildDeliveryPartnersUrl(),
        access_token
      );
      const { drivers, depots } = splitDeliveryPartners(extractOdooResults(parsed));
      setRetrieveDriversData(drivers);
      setRetrieveDepotsData(depots);
      saveDeliveryPartnersToStorage(drivers, depots);
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
      }
    }
  };

  const getRetrieveRoutes = async () => {
    if (!access_token || sessionExpired) {
      return;
    }
    try {
      const parsed = await odooApiFetch<{ results?: unknown[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?include_fields=${encodeURIComponent(
          "['id','name','state','picking_count','total_load_count',('stop_line_ids',('load_count',))]"
        )}`,
        access_token
      );
      setRetrieveRoutesData(extractOdooResults(parsed));
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        await handleSessionExpired();
      }
    }
  };

  useEffect(() => {
    if (!access_token || sessionExpired) {
      return;
    }
    let cancelled = false;
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([getDeliveryAreaApi(), getRetrieveRoutes()]);
      } finally {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    };
    void loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [access_token, sessionExpired]);

  useEffect(() => {
    if (isInitialLoading || !fromDate || !toDate || !access_token || sessionExpired) {
      return;
    }
    void fetchPickingOrdersForDateRange(fromDate, toDate);
  }, [fromDate, toDate, isInitialLoading, access_token, sessionExpired, userTimezone]);

  const handleRefresh = async () => {
    if (!access_token || sessionExpired || !fromDate || !toDate) {
      return;
    }
    setIsBoardBusy(true);
    try {
      await getRetrieveDeliveryPartners();
      await fetchPickingOrdersForDateRange(fromDate, toDate);
    } catch {
      toast.error("Could not refresh dashboard data.");
      setIsBoardBusy(false);
    }
  };

  const handleToggleItem = (itemId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const has = prev.includes(itemId);
      if (checked === has) {
        return prev;
      }
      return checked ? [...prev, itemId] : prev.filter((id) => id !== itemId);
    });
  };

  const handleOpenMoveDrawer = (item: any) => {
    setMoveTargetItem(item);
    setMoveDrawerOpen(true);
  };

  const handleSelectMoveArea = (toStageId: number | string, toStageTitle: string) => {
    if (!moveTargetItem || !activeStage) {
      return;
    }
    setMoveDrawerOpen(false);
    setPendingMove({
      picking: moveTargetItem,
      fromStageId: activeStage.id,
      fromStageTitle: activeStage.title,
      toStageId,
      toStageTitle,
    });
    setMoveTargetItem(null);
  };

  const handleConfirmMoveDeliveryArea = async () => {
    if (!pendingMove || !access_token || isUpdatingDeliveryArea) {
      return;
    }
    const { picking, fromStageId, fromStageTitle, toStageId, toStageTitle } =
      pendingMove;
    const deliveryAreaValue = toStageId === "none" ? false : (toStageId as number);
    const buildDeliveryAreaField = (stageId: number | string, title: string) =>
      stageId === "none" ? false : { id: stageId, name: title };
    const previousDeliveryArea = picking.delivery_area;

    setIsUpdatingDeliveryArea(true);
    setPickingOrdersData((prev) =>
      prev.map((record) =>
        record.id === picking.id
          ? {
              ...record,
              delivery_area: buildDeliveryAreaField(toStageId, toStageTitle),
            }
          : record
      )
    );
    setPendingMove(null);

    try {
      await updatePickingDeliveryArea(picking.id, deliveryAreaValue, access_token);
      toast.success(`Moved ${picking.name} to ${toStageTitle}.`);
    } catch (error) {
      setPickingOrdersData((prev) =>
        prev.map((record) =>
          record.id === picking.id
            ? {
                ...record,
                delivery_area:
                  previousDeliveryArea ??
                  buildDeliveryAreaField(fromStageId, fromStageTitle),
              }
            : record
        )
      );
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not update delivery area.",
      });
    } finally {
      setIsUpdatingDeliveryArea(false);
    }
  };

  const handleAssignRoute = async (formValues: AssignRouteFormValues) => {
    if (isAssigningRoute || selectedIds.length === 0) {
      return;
    }
    if (formValues.driverId === "") {
      toast.error("Please select a driver.");
      return;
    }
    if (formValues.mode === "existing" && formValues.routeId === "") {
      toast.error("Please select a route.");
      return;
    }
    setIsAssigningRoute(true);
    try {
      const data = await assignPickingsToRoute(
        buildAssignPickingsPayload(formValues, selectedIds),
        access_token
      );
      toast.success(data?.message || "Route assigned successfully!");
      setOpenAssignRouteModal(false);
      setSelectedIds([]);
      if (fromDate && toDate) {
        await fetchPickingOrdersForDateRange(fromDate, toDate);
      }
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Failed to assign route.",
      });
    } finally {
      setIsAssigningRoute(false);
    }
  };

  const filtersActive =
    assignmentFilter !== "unassigned" ||
    dateRangePreset !== "current_month" ||
    Boolean(searchInput.trim());

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f5f6fa",
          overflow: "hidden",
        }}
      >
        <MobilePageHeader
          title="Orders"
          subtitle={
            activeStage
              ? `${activeStage.title} · ${activeStage.items?.length ?? 0} orders`
              : sortedStages.length > 0
                ? "Select a delivery area"
                : "No delivery areas"
          }
          isBusy={isBoardBusy || isInitialLoading}
          filterActive={filtersActive}
          onOpenFilters={() => setFiltersOpen(true)}
          onOpenKnowledge={() => setKnowledgePanelOpen(true)}
          knowledgeActive={knowledgePanelOpen}
          onRefresh={() => void handleRefresh()}
          showSignOut={!embedded}
          onSignOut={() => {
            clearAuthData();
            navigate("/login");
          }}
        />

        {isInitialLoading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress sx={{ color: "#5fa67a" }} />
          </Box>
        ) : (
          <>
            <MobileAreaTabs
              stages={sortedStages}
              selectedStageId={selectedStageId}
              onSelectStage={(stageId) => {
                setSelectedStageId(stageId);
                setSelectedIds([]);
              }}
            />

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                px: 1.5,
                py: 1.25,
                pb: selectedIds.length > 0 ? 12 : 3,
              }}
            >
              {!activeStage ? (
                <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#6b7280", fontWeight: 600, mb: 0.75 }}
                  >
                    Select a delivery area
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    Choose a delivery area above to view its order list.
                  </Typography>
                </Box>
              ) : (activeStage.items?.length ?? 0) === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    No orders in {activeStage.title}
                  </Typography>
                </Box>
              ) : (
                (activeStage.items ?? []).map((item) => (
                  <MobileOrderCard
                    key={item.id}
                    item={item}
                    stageColor={activeStage.color}
                    userTimezone={userTimezone}
                    isSelected={selectedIds.includes(item.id)}
                    onToggle={handleToggleItem}
                    onMove={handleOpenMoveDrawer}
                  />
                ))
              )}
            </Box>
          </>
        )}

        {selectedIds.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 1,
              bgcolor: "#fff",
              borderTop: "1px solid #e5e9ef",
              boxShadow: "0 -8px 24px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => setOpenAssignRouteModal(true)}
              sx={{
                bgcolor: "#5fa67a",
                textTransform: "none",
                fontWeight: 700,
                py: 1.1,
                "&:hover": { bgcolor: "#4d9164" },
              }}
            >
              Assign route ({selectedIds.length})
            </Button>
          </Box>
        )}

        <MobileOrdersFilterDrawer
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          assignmentFilter={assignmentFilter}
          onAssignmentFilterChange={(value) => {
            setAssignmentFilter(value);
            setSelectedIds([]);
          }}
          dateRangePreset={dateRangePreset}
          onDateRangePresetChange={(preset) => {
            const { start, end } = getDateRangeForPreset(preset, userTimezone);
            setDateRangePreset(preset);
            setFromDate(start);
            setToDate(end);
          }}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={(value) => {
            if (!value) return;
            setDateRangePreset("");
            setFromDate(value.startOf("day"));
            if (toDate && value.isAfter(toDate, "day")) {
              setToDate(value.startOf("day"));
            }
          }}
          onToDateChange={(value) => {
            if (!value) return;
            setDateRangePreset("");
            setToDate(value.startOf("day"));
            if (fromDate && value.isBefore(fromDate, "day")) {
              setFromDate(value.startOf("day"));
            }
          }}
        />

        <MobileMoveAreaDrawer
          open={moveDrawerOpen}
          stages={sortedStages}
          currentStageId={activeStage?.id ?? ""}
          onClose={() => {
            setMoveDrawerOpen(false);
            setMoveTargetItem(null);
          }}
          onSelectArea={handleSelectMoveArea}
        />

        {openAssignRouteModal && (
          <AssignRouteModal
            openModal={openAssignRouteModal}
            isSubmitting={isAssigningRoute}
            isRefreshing={isRefreshingAssignData}
            routes={retrieveRoutesData}
            drivers={retrieveDriversData}
            depots={retrieveDepotsData}
            onRefresh={async () => {
              setIsRefreshingAssignData(true);
              try {
                await Promise.all([getRetrieveRoutes(), getRetrieveDeliveryPartners()]);
                toast.success("Routes and partners updated.");
              } catch {
                toast.error("Could not refresh routes and partners.");
              } finally {
                setIsRefreshingAssignData(false);
              }
            }}
            handleClose={() => !isAssigningRoute && setOpenAssignRouteModal(false)}
            handleAssignRoute={handleAssignRoute}
          />
        )}

        <MoveDeliveryAreaModal
          open={Boolean(pendingMove)}
          pendingMove={pendingMove}
          isSubmitting={isUpdatingDeliveryArea}
          userTimezone={userTimezone}
          embedded={embedded}
          onConfirm={() => void handleConfirmMoveDeliveryArea()}
          onCancel={() => !isUpdatingDeliveryArea && setPendingMove(null)}
        />

        <KnowledgePanel
          open={knowledgePanelOpen}
          onClose={() => setKnowledgePanelOpen(false)}
        />
      </Box>
    </LocalizationProvider>
  );
}
